import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  SlidersHorizontal,
  DownloadSimple,
  Sparkle,
  TrendUp,
  TrendDown,
  Broadcast,
  ChartBar,
  Users,
  Funnel,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { apiClient, useAuthStore } from '@dukene/shared';

import LiveView from '@/components/analytics/LiveView';
import SessionsChart from '@/components/analytics/SessionsChart';
import FunnelChart from '@/components/analytics/FunnelChart';
import AbandonedCartsTable from '@/components/analytics/AbandonedCartsTable';
import CartRecoveryMetrics from '@/components/analytics/CartRecoveryMetrics';

type TimeRange = '7d' | '30d' | '90d' | '365d';

interface AnalyticsStats {
  totalRevenue?: number;
  revenueChange?: number;
  totalOrders?: number;
  ordersChange?: number;
  totalCustomers?: number;
  customersChange?: number;
  averageOrderValue?: number;
  aovChange?: number;
  returningCustomerRate?: number;
  ordersFulfilled?: number;
  conversionRate?: number;
  netSales?: number;
  discounts?: number;
  returns?: number;
  shipping?: number;
  taxes?: number;
}

interface SalesPoint {
  date: string;
  revenue: number;
  previousRevenue?: number;
  orders: number;
  previousOrders?: number;
  aov?: number;
  previousAov?: number;
  sessions?: number;
  conversionRate?: number;
}

interface TopProduct {
  productId: string;
  title: string;
  sku: string;
  totalSales: number;
  revenue: number;
  previousRevenue?: number;
  image?: string;
}

/* ─────────────────────────────────────────────────────────────────────────
   CUSTOM TOOLTIP FOR SLEEK SHOPIFY RECHARTS
───────────────────────────────────────────────────────────────────────── */
function ShopifyTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#1C1F23] text-white px-3.5 py-2.5 rounded-xl shadow-xl border border-white/10 text-xs">
      <div className="font-semibold text-gray-300 mb-1.5 pb-1 border-b border-white/10">
        {label}
      </div>
      <div className="space-y-1">
        {payload.map((item: any, idx: number) => (
          <div key={idx} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-gray-300 capitalize">{item.name || item.dataKey}:</span>
            </div>
            <span className="font-mono font-bold text-white">
              {typeof item.value === 'number'
                ? item.name?.toLowerCase().includes('rate') || item.unit === '%'
                  ? `${item.value}%`
                  : item.value >= 1000
                  ? `KES ${item.value.toLocaleString()}`
                  : item.value.toLocaleString()
                : item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   MINI SVG SPARKLINE FOR KPI CARDS
───────────────────────────────────────────────────────────────────────── */
function MiniSparkline({
  data,
  color = '#006838',
  height = 24,
}: {
  data: number[];
  color?: string;
  height?: number;
}) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const width = 100;
  const padX = 4;
  const padY = 5;

  const points = data
    .map((val, idx) => {
      const x = padX + (idx / (data.length - 1)) * (width - padX * 2);
      const y = height - padY - ((val - min) / range) * (height - padY * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg className="w-24 h-6 overflow-hidden shrink-0" viewBox={`0 0 ${width} ${height}`}>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MAIN ANALYTICS COMPONENT
═══════════════════════════════════════════════════════════════════════ */
export default function Analytics() {
  const [activeTab, setActiveTab] = useState<'overview' | 'live' | 'sessions' | 'funnel' | 'recovery'>('overview');
  const [timeRange, setTimeRange] = useState<TimeRange>('365d');
  const [selectedKpi, setSelectedKpi] = useState<string>('gross_sales');
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  /* Fetch API Data */
  const { data: statsData } = useQuery<AnalyticsStats>({
    queryKey: ['analytics-stats', activeStoreId, timeRange],
    queryFn: async () => apiClient.get(`/analytics/stats?range=${timeRange}`),
    enabled: !!activeStoreId,
  });

  const { data: rawSales } = useQuery<SalesPoint[]>({
    queryKey: ['analytics-sales', activeStoreId, timeRange],
    queryFn: async () => apiClient.get(`/analytics/sales?range=${timeRange}`),
    enabled: !!activeStoreId,
  });

  const { data: apiTopProducts } = useQuery<TopProduct[]>({
    queryKey: ['analytics-top-products', activeStoreId, timeRange],
    queryFn: async () => apiClient.get(`/analytics/top-products?range=${timeRange}&limit=5`),
    enabled: !!activeStoreId,
  });

  const { data: sessionsData } = useQuery<any>({
    queryKey: ['analytics-sessions', activeStoreId, timeRange],
    queryFn: async () => apiClient.get(`/analytics/sessions?range=${timeRange}`),
    enabled: !!activeStoreId,
  });

  const { data: funnelData } = useQuery<any>({
    queryKey: ['analytics-funnel', activeStoreId, timeRange],
    queryFn: async () => apiClient.get(`/analytics/funnel?range=${timeRange}`),
    enabled: !!activeStoreId,
  });

  const { data: abandonedCartsData } = useQuery<any>({
    queryKey: ['analytics-abandoned-carts', activeStoreId, timeRange],
    queryFn: async () => apiClient.get(`/analytics/abandoned-carts?range=${timeRange}&limit=50`),
    enabled: !!activeStoreId,
  });

  const { data: cartRecoveryData } = useQuery<any>({
    queryKey: ['analytics-cart-recovery', activeStoreId, timeRange],
    queryFn: async () => apiClient.get(`/analytics/cart-recovery?range=${timeRange}`),
    enabled: !!activeStoreId,
  });

  /* ── Synthesis & Fallback Data ── */
  const timeline = useMemo(() => {
    return rawSales || [];
  }, [rawSales]);

  const stats = useMemo(() => {
    const totalRev = statsData?.totalRevenue || 0;
    return {
      grossSales: totalRev,
      grossSalesChange: statsData?.revenueChange ?? 0,
      returningCustomerRate: statsData?.returningCustomerRate ?? 0,
      returningCustomerChange: 0,
      ordersFulfilled: statsData?.ordersFulfilled ?? 0,
      ordersFulfilledChange: 0,
      orders: statsData?.totalOrders ?? 0,
      ordersChange: statsData?.ordersChange ?? 0,
      conversionRate: statsData?.conversionRate ?? 0,
      conversionRateChange: 0,
      aov: statsData?.averageOrderValue ?? 0,
      aovChange: statsData?.aovChange ?? 0,
      netSales: statsData?.netSales ?? statsData?.totalRevenue ?? 0,
      discounts: statsData?.discounts ?? 0,
      returns: statsData?.returns ?? 0,
      shipping: statsData?.shipping ?? 0,
      taxes: statsData?.taxes ?? 0,
    };
  }, [statsData]);

  const channelBreakdown = [
    { name: 'Online Store', value: stats.grossSales, color: '#2C6ECB' },
  ].filter(c => c.value > 0);

  const topProductsList = useMemo(() => {
    return apiTopProducts || [];
  }, [apiTopProducts]);

  const conversionFunnelStages = useMemo(() => {
    if (!funnelData) return [];
    const max = funnelData.started || 1;
    return [
      { stage: 'Sessions', count: funnelData.started || 0, percentage: 100, color: '#2C6ECB' },
      { stage: 'Added to cart', count: funnelData.addedToCart || 0, percentage: Number((((funnelData.addedToCart || 0) / max) * 100).toFixed(2)), color: '#3B82F6' },
      { stage: 'Reached checkout', count: funnelData.information || 0, percentage: Number((((funnelData.information || 0) / max) * 100).toFixed(2)), color: '#60A5FA' },
      { stage: 'Completed purchase', count: funnelData.completed || 0, percentage: Number((((funnelData.completed || 0) / max) * 100).toFixed(2)), color: '#006838' },
    ];
  }, [funnelData]);

  const kpiCards = [
    {
      id: 'gross_sales',
      label: 'Gross sales',
      value: `KES ${stats.grossSales.toLocaleString()}`,
      change: stats.grossSalesChange,
      sparkline: timeline.map((p) => p.revenue),
      color: '#006838',
    },
    {
      id: 'returning_customers',
      label: 'Returning customer rate',
      value: `${stats.returningCustomerRate}%`,
      change: stats.returningCustomerChange,
      sparkline: timeline.map((p) => p.conversionRate || 0), // Use meaningful sparkline if available
      color: '#2C6ECB',
    },
    {
      id: 'orders_fulfilled',
      label: 'Orders fulfilled',
      value: stats.ordersFulfilled.toLocaleString(),
      change: stats.ordersFulfilledChange,
      sparkline: timeline.map((p) => p.orders),
      color: '#006838',
    },
    {
      id: 'orders',
      label: 'Orders',
      value: stats.orders.toLocaleString(),
      change: stats.ordersChange,
      sparkline: timeline.map((p) => p.orders),
      color: '#006838',
    },
    {
      id: 'conversion_rate',
      label: 'Conversion rate',
      value: `${stats.conversionRate}%`,
      change: stats.conversionRateChange,
      sparkline: timeline.map((p) => p.conversionRate || 0),
      color: '#6438B5',
    },
    {
      id: 'aov',
      label: 'Average order value',
      value: `KES ${stats.aov.toLocaleString()}`,
      change: stats.aovChange,
      sparkline: timeline.map((p) => p.aov || 0),
      color: '#E58A00',
    },
  ];

  return (
    <div className="space-y-5 text-[#202223] dark:text-slate-100">
      
      {/* ── TOP SHOPIFY POLARIS ACTION HEADER ──────────────────────────────── */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
          
          {/* Title + Subnav */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div>
            <h1 className="text-base font-semibold text-[#202223] dark:text-white">
              {activeTab === 'overview' && 'Overview'}
              {activeTab === 'live' && 'Live View'}
              {activeTab === 'sessions' && 'Traffic & Sessions'}
              {activeTab === 'funnel' && 'Conversion Funnel'}
              {activeTab === 'recovery' && 'Abandoned Cart Recovery'}
            </h1>
          </div>

            {/* Sleek Polaris Tabs */}
            <div className="flex items-center gap-0.5 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: ChartBar },
                { id: 'live', label: 'Live View', icon: Broadcast },
                { id: 'sessions', label: 'Sessions', icon: Users },
                { id: 'funnel', label: 'Funnel', icon: Funnel },
                { id: 'recovery', label: 'Recovery', icon: ArrowCounterClockwise },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as any)}
                  className={`h-8 flex items-center gap-1.5 px-3 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    activeTab === id
                      ? 'bg-[#E3F1DF] text-[#006838] dark:bg-[#006838]/20 dark:text-emerald-400'
                      : 'text-[#5C5F62] dark:text-slate-400 hover:bg-[#F6F6F7] dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon weight={activeTab === id ? 'fill' : 'regular'} className="w-3.5 h-3.5" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Right Controls: Date Range Pill + Compare Pill + Actions */}
          <div className="flex items-center flex-wrap gap-2">
            {/* Date Range Button */}
            <div className="h-8 inline-flex items-center bg-white dark:bg-slate-800 border border-[#D2D5D9] dark:border-slate-700 rounded-lg text-xs px-2.5">
              <Calendar className="w-4 h-4 text-gray-500 mr-2" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                className="bg-transparent border-none text-[#202223] dark:text-white font-semibold focus:outline-none cursor-pointer pr-1"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="365d">Last 365 days</option>
              </select>
            </div>

            {/* Compare To Pill */}
            <div className="hidden">
              <span>Compare to: <strong className="text-black dark:text-white">Feb 14, 2023 – Feb 12, 2024</strong></span>
            </div>

            {/* Customize / Action buttons */}
            <div className="flex items-center gap-2">
              <button
                title="Filter analytics"
                className="hidden"
              >
                <SlidersHorizontal className="w-4 h-4" />
              </button>
              <button
                onClick={() => alert('Customizing Analytics Layout...')}
                className="hidden"
              >
                <Sparkle weight="fill" className="w-3.5 h-3.5 text-emerald-400 dark:text-emerald-600" />
                <span>Customize</span>
              </button>
              <button
                onClick={() => window.print()}
                className="h-8 px-3 bg-white dark:bg-slate-800 border border-[#D2D5D9] dark:border-slate-700 rounded-lg text-[#202223] dark:text-white hover:bg-[#F6F6F7] text-xs font-semibold inline-flex items-center gap-1.5"
              >
                <DownloadSimple className="w-3.5 h-3.5 text-[#006838]" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Subnav Strip */}
        <div className="hidden">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'live', label: 'Live View' },
            { id: 'sessions', label: 'Sessions' },
            { id: 'funnel', label: 'Funnel' },
            { id: 'recovery', label: 'Recovery' },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as any)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap ${
                activeTab === id
                  ? 'bg-black text-white dark:bg-white dark:text-black'
                  : 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── TAB CONTENT AREA ───────────────────────────────────────────────── */}
      <div>
        {activeTab === 'live' && <LiveView />}
        {activeTab === 'sessions' && <SessionsChart data={sessionsData} />}
        {activeTab === 'funnel' && <FunnelChart data={funnelData} />}
        {activeTab === 'recovery' && (
          <div className="space-y-5">
            <CartRecoveryMetrics data={cartRecoveryData} />
            <AbandonedCartsTable data={abandonedCartsData} />
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            SHOPIFY ANALYTICS OVERVIEW TAB (THE 1:1 SLEEK COPY)
        ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            
            {/* 1. TOP KPI METRIC CARDS STRIP (Horizontal Grid with Sparklines) */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {kpiCards.map((card) => {
                const isSelected = selectedKpi === card.id;
                const isPositive = card.change >= 0;
                return (
                  <div
                    key={card.id}
                    onClick={() => setSelectedKpi(card.id)}
                    className={`bg-white dark:bg-[#1A1D21] rounded-xl p-3.5 border transition-colors cursor-pointer overflow-hidden ${
                      isSelected
                        ? 'border-[#006838] ring-1 ring-[#006838]/20 shadow-sm'
                        : 'border-[#D2D5D9] dark:border-slate-800 hover:border-[#8C9196] shadow-sm'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 truncate">
                        {card.label}
                      </span>
                      <span
                        className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                          isPositive
                            ? 'bg-emerald-50 dark:bg-emerald-950/40 text-[#006838] dark:text-emerald-400'
                            : 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400'
                        }`}
                      >
                        {isPositive ? (
                          <TrendUp weight="bold" className="w-3 h-3" />
                        ) : (
                          <TrendDown weight="bold" className="w-3 h-3" />
                        )}
                        {card.change}%
                      </span>
                    </div>

                    <div className="flex items-end justify-between gap-3">
                      <div className="text-lg font-bold font-mono text-[#202223] dark:text-white leading-tight truncate">
                        {card.value}
                      </div>
                      <MiniSparkline data={card.sparkline} color={card.color} height={22} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 2. MODULAR GRID ROW 1: TOTAL SALES OVER TIME (2/3) + TOTAL SALES BREAKDOWN (1/3) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              
              {/* Left Card: Total sales over time */}
              <div className="lg:col-span-2 bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-[#1A1C1D] dark:text-white">
                      Total sales over time
                    </h3>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-xl font-bold font-mono text-[#202223] dark:text-white">
                        KES {stats.grossSales.toLocaleString()}
                      </span>
                      <span className="text-xs text-[#006838] font-bold bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded">
                        ↗ {stats.grossSalesChange}%
                      </span>
                    </div>
                  </div>

                  <div className="hidden md:flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1.5 font-semibold text-[#2C6ECB]">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#2C6ECB]" />
                      <span>Feb 14, 2024 – Today</span>
                    </div>
                    <div className="flex items-center gap-1.5 font-medium text-gray-400">
                      <span className="w-2.5 h-2.5 rounded-full bg-gray-400 border border-dashed" />
                      <span>Comparison period</span>
                    </div>
                  </div>
                </div>

                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={timeline} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="shopifySalesGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2C6ECB" stopOpacity={0.18} />
                          <stop offset="95%" stopColor="#2C6ECB" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E1E3E5" opacity={0.5} />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6B7280', fontSize: 11 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6B7280', fontSize: 11 }}
                        tickFormatter={(val) => `${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                      />
                      <Tooltip content={<ShopifyTooltip />} />
                      {/* Previous Period Dashed Line */}
                      <Line
                        type="monotone"
                        dataKey="previousRevenue"
                        name="Previous period"
                        stroke="#94A3B8"
                        strokeWidth={1.5}
                        strokeDasharray="4 4"
                        dot={false}
                      />
                      {/* Current Period Area & Line */}
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        name="Gross sales"
                        stroke="#2C6ECB"
                        strokeWidth={2.5}
                        fill="url(#shopifySalesGrad)"
                        dot={false}
                        activeDot={{ r: 5, fill: '#2C6ECB', strokeWidth: 2, stroke: '#fff' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Right Card: Total sales breakdown (Ledger Table) */}
              <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <h3 className="text-sm font-bold text-[#1A1C1D] dark:text-white mb-4">
                    Total sales breakdown
                  </h3>

                  <div className="space-y-3 divide-y divide-gray-100 dark:divide-white/5">
                    {[
                      { label: 'Gross sales', value: stats.grossSales, change: '', isPositive: true },
                      { label: 'Discounts', value: -stats.discounts, change: '', isPositive: true },
                      { label: 'Returns', value: -stats.returns, change: '', isPositive: false },
                      { label: 'Net sales', value: stats.netSales, change: '', isPositive: true, bold: true },
                      { label: 'Shipping charges', value: stats.shipping, change: '', isPositive: true },
                      { label: 'Return fees', value: 0, change: '—', isPositive: true },
                      { label: 'Taxes', value: stats.taxes, change: '', isPositive: true },
                    ].map((row, idx) => (
                      <div key={idx} className={`flex items-center justify-between pt-2.5 ${idx === 0 ? 'pt-0' : ''}`}>
                        <span className={`text-xs ${row.bold ? 'font-bold text-black dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                          {row.label}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-mono ${row.bold ? 'font-bold text-black dark:text-white' : 'text-gray-800 dark:text-gray-200'}`}>
                            {row.value < 0 ? `-KES ${Math.abs(row.value).toLocaleString()}` : `KES ${row.value.toLocaleString()}`}
                          </span>
                          <span className={`text-[11px] font-semibold ${row.isPositive ? 'text-[#006838] dark:text-emerald-400' : 'text-red-500'}`}>
                            {row.change}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total bottom banner */}
                <div className="mt-4 pt-3 border-t-2 border-gray-200 dark:border-white/10 flex items-center justify-between">
                  <span className="text-xs font-bold text-black dark:text-white uppercase tracking-wider">
                    Total sales
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold font-mono text-[#006838] dark:text-emerald-400">
                      KES {stats.grossSales.toLocaleString()}
                    </span>
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${stats.grossSalesChange >= 0 ? 'text-[#006838] bg-emerald-50 dark:bg-emerald-950/40' : 'text-red-500 bg-red-50 dark:bg-red-950/40'}`}>
                      {stats.grossSalesChange >= 0 ? '↗' : '↘'} {Math.abs(stats.grossSalesChange)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. MODULAR GRID ROW 2: SALES BY CHANNEL + AOV OVER TIME + SALES BY PRODUCT */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              
              {/* Card 1: Total sales by sales channel */}
              <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm flex flex-col justify-between">
                <h3 className="text-sm font-bold text-[#1A1C1D] dark:text-white mb-2">
                  Total sales by sales channel
                </h3>

                <div className="relative h-48 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={channelBreakdown}
                        innerRadius={56}
                        outerRadius={78}
                        paddingAngle={3}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {channelBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<ShopifyTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Center callout exactly like Shopify screenshot */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-base font-bold font-mono text-black dark:text-white">
                      KES {(stats.grossSales / 1000).toFixed(0)}K
                    </span>
                    <span className={`text-[10px] font-bold ${stats.grossSalesChange >= 0 ? 'text-[#006838]' : 'text-red-500'}`}>
                      {stats.grossSalesChange >= 0 ? '↗' : '↘'} {Math.abs(stats.grossSalesChange)}%
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-white/5">
                  {channelBreakdown.map((ch, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: ch.color }} />
                      <span className="text-gray-600 dark:text-gray-300 truncate">{ch.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Card 2: Average order value over time */}
              <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm overflow-hidden">
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-[#1A1C1D] dark:text-white">
                    Average order value over time
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xl font-bold font-mono text-black dark:text-white">
                      KES {stats.aov.toLocaleString()}
                    </span>
                    <span className={`text-xs font-bold ${stats.aovChange >= 0 ? 'text-[#006838]' : 'text-red-500'}`}>
                      {stats.aovChange >= 0 ? '↗' : '↘'} {Math.abs(stats.aovChange)}%
                    </span>
                  </div>
                </div>

                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={timeline} margin={{ top: 15, right: 15, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E1E3E5" opacity={0.4} />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 10 }} width={35} />
                      <Tooltip content={<ShopifyTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="previousAov"
                        name="Previous period"
                        stroke="#94A3B8"
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="aov"
                        name="Average order value"
                        stroke="#E58A00"
                        strokeWidth={1.75}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Card 3: Total sales by product (Horizontal Bar Comparison) */}
              <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[#1A1C1D] dark:text-white">
                    Total sales by product
                  </h3>
                  <span className="text-[11px] text-gray-400">Best Sellers</span>
                </div>

                <div className="space-y-4 my-2">
                  {topProductsList.map((prod) => (
                    <div key={prod.productId} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 truncate pr-2">
                          {prod.title}
                        </span>
                        <span className="font-mono font-bold text-[#006838]">
                          KES {Number(prod.revenue).toLocaleString()}
                        </span>
                      </div>
                      {/* Dual comparison bar exactly like Shopify screenshot */}
                      <div className="h-2 w-full bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden flex">
                        <div
                          className="h-full bg-[#2C6ECB] rounded-full transition-all"
                          style={{ width: `${Math.min(100, (prod.revenue / 6000) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between text-[11px] text-gray-400 pt-2 border-t border-gray-100 dark:border-white/5">
                  <span>Showing top {topProductsList.length} items</span>
                  <span>Compared to previous period</span>
                </div>
              </div>
            </div>

            {/* 4. MODULAR GRID ROW 3: SESSIONS OVER TIME + CONVERSION RATE OVER TIME + CONVERSION FUNNEL */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              
              {/* Card 1: Sessions over time */}
              <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm overflow-hidden">
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-[#1A1C1D] dark:text-white">
                    Sessions over time
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xl font-bold font-mono text-black dark:text-white">
                      {(sessionsData?.totalSessions || 0).toLocaleString()}
                    </span>
                    <span className={`text-xs font-bold ${(sessionsData?.sessionsChange || 0) >= 0 ? 'text-[#006838]' : 'text-red-500'}`}>
                      {(sessionsData?.sessionsChange || 0) >= 0 ? '↗' : '↘'} {Math.abs(sessionsData?.sessionsChange || 0)}%
                    </span>
                  </div>
                </div>

                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={timeline} margin={{ top: 15, right: 15, left: 5, bottom: 5 }}>
                      <defs>
                        <linearGradient id="sessionGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6438B5" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#6438B5" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E1E3E5" opacity={0.4} />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 10 }} width={35} />
                      <Tooltip content={<ShopifyTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="sessions"
                        name="Sessions"
                        stroke="#6438B5"
                        strokeWidth={1.75}
                        fill="url(#sessionGrad)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Card 2: Conversion rate over time */}
              <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm overflow-hidden">
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-[#1A1C1D] dark:text-white">
                    Conversion rate over time
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xl font-bold font-mono text-black dark:text-white">
                      {stats.conversionRate}%
                    </span>
                    <span className={`text-xs font-bold ${stats.conversionRateChange >= 0 ? 'text-[#006838]' : 'text-red-500'}`}>
                      {stats.conversionRateChange >= 0 ? '↗' : '↘'} {Math.abs(stats.conversionRateChange)}%
                    </span>
                  </div>
                </div>

                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={timeline} margin={{ top: 15, right: 15, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E1E3E5" opacity={0.4} />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 10 }} width={35} />
                      <Tooltip content={<ShopifyTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="conversionRate"
                        name="Conversion rate"
                        unit="%"
                        stroke="#006838"
                        strokeWidth={1.75}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Card 3: Conversion rate breakdown (Sleek Funnel bars) */}
              <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm flex flex-col justify-between">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[#1A1C1D] dark:text-white">
                    Conversion rate breakdown
                  </h3>
                  <span className="text-xs font-bold text-[#006838]">1.36% overall</span>
                </div>

                <div className="space-y-3 my-2">
                  {conversionFunnelStages.map((stage, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {stage.stage}
                        </span>
                        <div className="flex items-center gap-3 font-mono">
                          <span className="text-gray-400">{(Number(stage.count) || 0).toLocaleString()}</span>
                          <span className="font-bold text-black dark:text-white w-12 text-right">
                            {stage.percentage}%
                          </span>
                        </div>
                      </div>
                      <div className="h-2 w-full bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.max(4, stage.percentage)}%`,
                            backgroundColor: stage.color,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between text-[11px] text-gray-400 pt-2 border-t border-gray-100 dark:border-white/5">
                  <span>Funnel completion efficiency</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Healthy</span>
                </div>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
